import React from 'react';
import { Settings, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';

/**
 * ExecutionRiskFactors - Reusable component for execution risk configuration
 * 
 * @param {Object} props
 * @param {Object} props.values - Current values { adoption_speed, leadership_alignment, operational_readiness }
 * @param {Function} props.onChange - Callback when values change: (field, value) => void
 * @param {string} [props.variant='default'] - Visual variant: 'default', 'compact', 'minimal'
 * @param {boolean} [props.showCard=true] - Whether to wrap in a Card component
 * @param {string} [props.className] - Additional CSS classes
 */
export const ExecutionRiskFactors = ({ 
  values = { adoption_speed: 0.3, leadership_alignment: 0.2, operational_readiness: 0.25 },
  onChange,
  variant = 'default',
  showCard = true,
  className = ''
}) => {
  // Calculate impact multiplier
  const impactMultiplier = Math.max(
    0.1, 
    1.0 - ((values.adoption_speed * 0.4 + values.leadership_alignment * 0.3 + values.operational_readiness * 0.3) * 0.9)
  );

  const riskFactors = [
    {
      id: 'adoption_speed',
      label: 'Slow Adoption Risk',
      description: 'Risk of delayed or incomplete adoption of learning outcomes',
      value: values.adoption_speed,
      testId: 'adoption-speed-slider'
    },
    {
      id: 'leadership_alignment',
      label: 'Leadership Misalignment Risk',
      description: 'Risk of weak executive sponsorship or competing priorities',
      value: values.leadership_alignment,
      testId: 'leadership-alignment-slider'
    },
    {
      id: 'operational_readiness',
      label: 'Operational Barriers Risk',
      description: 'Risk from infrastructure gaps, process friction, or change resistance',
      value: values.operational_readiness,
      testId: 'operational-readiness-slider'
    }
  ];

  // Compact variant (for collapsible sections)
  if (variant === 'compact') {
    return (
      <div className={`space-y-4 ${className}`}>
        {riskFactors.map(factor => (
          <div key={factor.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">{factor.label}</Label>
              <span className="text-xs font-mono text-amber-700">
                {(factor.value * 100).toFixed(0)}% risk
              </span>
            </div>
            <Slider
              value={[factor.value * 100]}
              onValueChange={([v]) => onChange(factor.id, v / 100)}
              max={100}
              min={0}
              step={5}
              data-testid={factor.testId}
              className="w-full"
            />
          </div>
        ))}
        <div className="pt-2 border-t border-amber-200">
          <p className="text-xs text-amber-700">
            Impact multiplier: <span className="font-bold">{(impactMultiplier * 100).toFixed(0)}%</span> of theoretical impact
          </p>
        </div>
      </div>
    );
  }

  // Minimal variant (inline, no descriptions)
  if (variant === 'minimal') {
    return (
      <div className={`space-y-3 ${className}`}>
        {riskFactors.map(factor => (
          <div key={factor.id} className="flex items-center gap-4">
            <Label className="text-xs w-40 flex-shrink-0">{factor.label}</Label>
            <Slider
              value={[factor.value * 100]}
              onValueChange={([v]) => onChange(factor.id, v / 100)}
              max={100}
              min={0}
              step={5}
              data-testid={factor.testId}
              className="flex-1"
            />
            <span className="text-[10px] font-mono text-amber-700 w-14 text-right">
              {(factor.value * 100).toFixed(0)}% risk
            </span>
          </div>
        ))}
        <p className="text-[10px] text-amber-600">
          Impact multiplier: {(impactMultiplier * 100).toFixed(0)}%
        </p>
      </div>
    );
  }

  // Default content (can be wrapped in card or not)
  const content = (
    <div className="space-y-6">
      {riskFactors.map(factor => (
        <div key={factor.id} className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>{factor.label}</Label>
            <span className="text-sm font-mono text-amber-700">
              {(factor.value * 100).toFixed(0)}% risk
            </span>
          </div>
          <Slider
            value={[factor.value]}
            onValueChange={([v]) => onChange(factor.id, v)}
            max={1}
            min={0}
            step={0.05}
            data-testid={factor.testId}
          />
          <p className="text-xs text-slate-500">{factor.description}</p>
        </div>
      ))}
      
      <div className="pt-3 border-t border-amber-200">
        <p className="text-xs text-amber-700">
          Impact multiplier: <span className="font-bold">{(impactMultiplier * 100).toFixed(0)}%</span> of theoretical impact
        </p>
      </div>
    </div>
  );

  if (!showCard) {
    return <div className={className}>{content}</div>;
  }

  return (
    <Card className={`border-amber-200 bg-gradient-to-br from-amber-50/30 to-white ${className}`}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Settings className="h-5 w-5 text-amber-600" />
          Execution Risk Factors
        </CardTitle>
        <CardDescription>
          Factors that may reduce value transfer between learning and business outcomes. Higher % = more impact reduction.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};

/**
 * ExecutionRiskCollapsible - Collapsible version for compact layouts
 * Used in PortfolioAnalysisPage and FullMatrixPage
 */
export const ExecutionRiskCollapsible = ({
  values,
  onChange,
  isOpen,
  onToggle,
  className = ''
}) => {
  const impactMultiplier = Math.max(
    0.1, 
    1.0 - ((values.adoption_speed * 0.4 + values.leadership_alignment * 0.3 + values.operational_readiness * 0.3) * 0.9)
  );

  return (
    <div className={`bg-amber-50/50 rounded-lg border border-amber-200 overflow-hidden ${className}`}>
      {/* Collapsible Header */}
      <div 
        className="px-3 py-2 cursor-pointer hover:bg-amber-100/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <Label className="text-sm font-semibold cursor-pointer">Execution Risk Factors</Label>
        </div>
        <p className="text-xs text-amber-600 mt-1">
          Impact multiplier: {(impactMultiplier * 100).toFixed(0)}% of theoretical impact
        </p>
      </div>
      
      {/* Collapsible Content */}
      {isOpen && (
        <div className="px-3 pb-3 space-y-3 border-t border-amber-200 pt-3">
          <p className="text-xs text-amber-700">
            Factors that may reduce value transfer between learning and business outcomes.
          </p>
          <ExecutionRiskFactors
            values={values}
            onChange={onChange}
            variant="compact"
            showCard={false}
          />
        </div>
      )}
    </div>
  );
};

export default ExecutionRiskFactors;
